type FilterOption = {
    id: string;
    label: string;
    value: string;
    count?: number;
};

/**
 * Merges primary filter options with a fallback set to ensure applied filter
 * values are always present. Primary options keep their counts (reflecting the
 * current search). Missing applied values are added from the fallback with count 0.
 *
 * Use this when a search query narrows results to zero, which can cause
 * applied filter values to disappear from the options list.
 *
 * Pass `sortByLabel: true` to override the input ordering and sort the merged
 * result alphabetically by label (useful for fixed-set filters where count-DESC
 * ordering from the DB is not meaningful). Pass `locale` with it so collation
 * follows the app language, not the host/browser one.
 *
 * Pass `sortOrder` for a fixed set whose meaningful order is neither count nor
 * alphabet, e.g. a status that follows a lifecycle. Values missing from the list
 * keep their input order behind the listed ones.
 */
export function mergeFilterOptions<T>(
    primary: T[] | undefined,
    fallback: T[] | undefined,
    mapper: (item: T) => FilterOption,
    appliedValues: string[] | null | undefined,
    options?: { sortByLabel?: boolean; locale?: string; sortOrder?: readonly string[] },
): FilterOption[] {
    const result = [...(primary?.map(mapper) ?? [])];

    if (appliedValues?.length && fallback?.length) {
        const existingValues = new Set(result.map((o) => o.value));
        const missing = appliedValues.filter((v) => !existingValues.has(v));

        if (missing.length > 0) {
            const mappedFallback = fallback.map(mapper);
            const fallbackByValue = new Map(mappedFallback.map((o) => [o.value, o]));

            for (const value of missing) {
                const option = fallbackByValue.get(value);
                if (option) {
                    result.push({ ...option, count: 0 });
                }
            }
        }
    }

    const { sortOrder } = options ?? {};
    if (sortOrder) {
        // toSorted is stable, so unlisted values keep the order the query returned them in.
        const rank = (value: string): number => {
            const index = sortOrder.indexOf(value);
            return index === -1 ? sortOrder.length : index;
        };
        return result.toSorted((a, b) => rank(a.value) - rank(b.value));
    }

    if (options?.sortByLabel) {
        return result.toSorted((a, b) => a.label.localeCompare(b.label, options.locale));
    }

    return result;
}
